/**
 * CompanionDashboard - Interface do Companion (Celular do Motorista)
 * Módulo Motorista - Sistema IDOOH
 * 
 * Funcionalidades:
 * - Visualização de packs de mídias
 * - Input para código de pareamento (conectar tablet)
 * - Ativação/desativação de tablet
 * - Gerenciamento de mídias ativas
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
    activatePairing,
    getDriverPairings,
    deactivatePairing,
    DevicePairing,
    cleanPairingCode,
    formatPairingCode
} from '../services/pairingService';
import {
    Loader2,
    Tablet,
    Power,
    LogOut,
    Wifi,
    WifiOff,
    Trash2,
    AlertCircle,
    Package,
    BarChart3,
    DollarSign,
    Search,
    X,
    Plus,
    ChevronRight
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useDashCompanionData } from '../hooks/useDashCompanionData';
import { MediaPackCard } from './MediaPackCard';
import { MediaPackExpanded } from './MediaPackExpanded';
import { ActiveMediaList } from './ActiveMediaList';

export function CompanionDashboard() {
    const navigate = useNavigate();

    // Estados de autenticação e pareamento
    const [pairingCode, setPairingCode] = useState('');
    const [isActivating, setIsActivating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [driverId, setDriverId] = useState<string | null>(null);
    const [driverName, setDriverName] = useState<string>('');
    const [pairedDevices, setPairedDevices] = useState<DevicePairing[]>([]);

    // Estados do modal de conectar tablet
    const [showConnectModal, setShowConnectModal] = useState(false);

    // Hook de dados das campanhas
    const {
        packs,
        activeMedias,
        selectedPack,
        searchQuery,
        isLoading: isLoadingMedias,
        stats,
        setSearchQuery,
        removeMedia,
        activatePack,
        openPack,
        closePack
    } = useDashCompanionData();

    // Carregar dados do motorista e dispositivos pareados
    useEffect(() => {
        async function loadData() {
            try {
                setIsLoading(true);

                const { data: { user } } = await supabase.auth.getUser();

                if (!user) {
                    navigate('/driver/login', { replace: true });
                    return;
                }

                // Verificar se é motorista
                if (user.user_metadata?.role !== 'driver') {
                    navigate('/', { replace: true });
                    return;
                }

                // Verificar status do perfil
                const { data: driver } = await supabase
                    .from('drivers')
                    .select('profile_status')
                    .eq('id', user.id)
                    .single();

                if (driver) {
                    if (driver.profile_status === 'pending_correction') {
                        navigate('/driver/correction', { replace: true });
                        return;
                    }
                    if (driver.profile_status === 'rejected') {
                        navigate('/driver/rejected', { replace: true });
                        return;
                    }
                    if (driver.profile_status === 'pending_review') {
                        navigate('/driver/review', { replace: true });
                        return;
                    }
                    if (driver.profile_status === 'pending_submission') {
                        navigate('/driver/onboarding', { replace: true });
                        return;
                    }
                }

                setDriverId(user.id);
                setDriverName(user.user_metadata?.full_name || 'Motorista');

                // Buscar dispositivos pareados
                const devices = await getDriverPairings(user.id);
                setPairedDevices(devices);

            } catch (error) {
                console.error('[CompanionDashboard] Erro ao carregar:', error);
            } finally {
                setIsLoading(false);
            }
        }

        loadData();
    }, [navigate]);

    // Listener em tempo real para mudanças no status
    useEffect(() => {
        const channel = supabase.channel('driver-status-changes')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'drivers'
                },
                async (payload) => {
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user && payload.new.id === user.id) {
                        const newStatus = payload.new.profile_status;
                        console.log('[CompanionDashboard] Status alterado para:', newStatus);

                        if (newStatus === 'pending_correction') {
                            toast.info('Você tem uma notificação de correção');
                            navigate('/driver/correction', { replace: true });
                        } else if (newStatus === 'rejected') {
                            navigate('/driver/rejected', { replace: true });
                        }
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [navigate]);

    // Formatar código enquanto digita
    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        const cleaned = cleanPairingCode(value);

        if (cleaned.length <= 8) {
            if (cleaned.length > 4) {
                setPairingCode(`${cleaned.substring(0, 4)}-${cleaned.substring(4)}`);
            } else {
                setPairingCode(cleaned);
            }
        }
    };

    // Ativar tablet
    const handleActivate = async () => {
        if (!driverId) {
            toast.error('Erro de autenticação. Faça login novamente.');
            return;
        }

        const cleanedCode = cleanPairingCode(pairingCode);

        if (cleanedCode.length !== 8) {
            toast.error('Digite o código completo de 8 caracteres');
            return;
        }

        try {
            setIsActivating(true);

            const result = await activatePairing(cleanedCode, driverId);

            if (result.success) {
                toast.success(result.message || 'Tablet ativado com sucesso!');
                setPairingCode('');
                setShowConnectModal(false);

                // Recarregar lista de dispositivos
                const devices = await getDriverPairings(driverId);
                setPairedDevices(devices);
            } else {
                toast.error(result.error || 'Erro ao ativar tablet');
            }

        } catch (error) {
            console.error('[CompanionDashboard] Erro ao ativar:', error);
            toast.error('Erro de conexão. Tente novamente.');
        } finally {
            setIsActivating(false);
        }
    };

    // Desativar tablet
    const handleDeactivate = async (pairing: DevicePairing) => {
        try {
            const success = await deactivatePairing(pairing.id);

            if (success) {
                toast.success('Tablet desconectado com sucesso');
                setPairedDevices(prev => prev.filter(p => p.id !== pairing.id));
            } else {
                toast.error('Erro ao desconectar tablet');
            }
        } catch (error) {
            console.error('[CompanionDashboard] Erro ao desativar:', error);
        }
    };

    // Logout
    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/driver/login', { replace: true });
    };

    // Ativar pack
    const handleActivatePack = (pack: typeof packs[0]) => {
        const count = activatePack(pack);
        if (count > 0) {
            toast.success(`${count} mídia(s) adicionada(s) à veiculação!`);
        } else {
            toast.info('Todas as mídias deste pack já estão em veiculação');
        }
    };

    // Remover mídia
    const handleRemoveMedia = (mediaId: string) => {
        removeMedia(mediaId);
        toast.success('Mídia removida da veiculação');
    };

    // Limpar busca
    const clearSearch = () => {
        setSearchQuery('');
    };

    const activeDevice = pairedDevices.find(d => d.status === 'active');

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <div className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 pt-4 pb-3 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Olá, {driverName}</h1>
                        <p className="text-gray-500 text-sm">Gerencie suas campanhas e tablet</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Status conexão tablet */}
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${activeDevice
                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                            }`}>
                            {activeDevice ? (
                                <>
                                    <Wifi className="w-3.5 h-3.5" />
                                    <span>Tablet OK</span>
                                </>
                            ) : (
                                <>
                                    <WifiOff className="w-3.5 h-3.5" />
                                    <span>Sem tablet</span>
                                </>
                            )}
                        </div>
                        {/* Logout */}
                        <button
                            onClick={handleLogout}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                            title="Sair"
                        >
                            <LogOut className="w-5 h-5 text-gray-600" />
                        </button>
                    </div>
                </div>

                {/* SearchBox */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar por ID da mídia..."
                        className="w-full pl-10 pr-10 py-3 bg-emerald-50 border border-emerald-100 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
                    />
                    {searchQuery && (
                        <button
                            onClick={clearSearch}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-emerald-100 rounded-full"
                        >
                            <X className="w-4 h-4 text-gray-400" />
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="px-4 pb-6 space-y-6 pt-4">

                {/* Card de Conexão do Tablet */}
                <div className={`rounded-2xl p-4 shadow-lg border ${activeDevice
                    ? 'bg-gradient-to-br from-emerald-500 to-green-600 border-emerald-400'
                    : 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300'
                    }`}>
                    {activeDevice ? (
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                    <Tablet className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <p className="text-white font-semibold">Tablet Conectado</p>
                                    <p className="text-white/80 text-sm font-mono">{formatPairingCode(activeDevice.pairing_code)}</p>
                                </div>
                            </div>

                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <button className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
                                        <Trash2 className="w-5 h-5 text-white" />
                                    </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-white rounded-2xl border-0 shadow-2xl">
                                    <AlertDialogHeader>
                                        <AlertDialogTitle className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <AlertCircle className="w-6 h-6 text-red-500" />
                                            Desconectar Tablet?
                                        </AlertDialogTitle>
                                        <AlertDialogDescription className="text-gray-500 text-base">
                                            Ao desconectar, o tablet deixará de exibir mídias e seus ganhos serão pausados.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter className="flex-col gap-2 space-y-2 mt-4">
                                        <AlertDialogAction
                                            onClick={() => handleDeactivate(activeDevice)}
                                            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-xl border-0"
                                        >
                                            SIM, DESCONECTAR
                                        </AlertDialogAction>
                                        <AlertDialogCancel className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 rounded-xl border-0">
                                            Cancelar
                                        </AlertDialogCancel>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowConnectModal(true)}
                            className="w-full flex items-center justify-between py-2"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                                    <Plus className="w-6 h-6 text-emerald-600" />
                                </div>
                                <div className="text-left">
                                    <p className="text-gray-800 font-semibold">Conectar Tablet</p>
                                    <p className="text-gray-500 text-sm">Adicione um dispositivo para exibir mídias</p>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                        </button>
                    )}
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-3 text-center shadow-lg">
                        <div className="flex items-center justify-center gap-1 text-white/80 text-xs mb-1">
                            <BarChart3 className="w-3.5 h-3.5" />
                            <span>Ativas</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{stats.totalActive}</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-3 text-center shadow-lg">
                        <div className="flex items-center justify-center gap-1 text-white/80 text-xs mb-1">
                            <Package className="w-3.5 h-3.5" />
                            <span>Total</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{stats.totalMedias}</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-3 text-center shadow-lg">
                        <div className="flex items-center justify-center gap-1 text-white/80 text-xs mb-1">
                            <DollarSign className="w-3.5 h-3.5" />
                            <span>Ganhos</span>
                        </div>
                        <p className="text-lg font-bold text-white">R${stats.totalEarnings.toFixed(0)}</p>
                    </div>
                </div>

                {/* Seção Pack de Mídias */}
                {packs.length > 0 && !searchQuery && (
                    <div className="space-y-3">
                        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 px-1">
                            <Package className="w-5 h-5 text-emerald-500" />
                            Pack Disponível
                        </h2>
                        {packs.map(pack => (
                            <MediaPackCard
                                key={pack.id}
                                pack={pack}
                                onOpen={openPack}
                            />
                        ))}
                    </div>
                )}

                {/* Lista de Mídias Ativas */}
                <ActiveMediaList
                    medias={activeMedias}
                    onRemove={handleRemoveMedia}
                />
            </div>

            {/* Modal Pack Expandido */}
            {selectedPack && (
                <MediaPackExpanded
                    pack={selectedPack}
                    onClose={closePack}
                    onActivate={handleActivatePack}
                />
            )}

            {/* Modal de Conectar Tablet */}
            {showConnectModal && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center sm:items-center">
                    <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl animate-slide-up">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-800">Conectar Tablet</h2>
                            <button
                                onClick={() => setShowConnectModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="flex flex-col items-center text-center mb-6">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                                <Tablet className="w-8 h-8 text-emerald-600" />
                            </div>
                            <p className="text-gray-500 text-sm">
                                Digite o código que aparece na tela do tablet
                            </p>
                        </div>

                        <div className="space-y-4">
                            <input
                                type="text"
                                value={pairingCode}
                                onChange={handleCodeChange}
                                placeholder="XXXX-XXXX"
                                maxLength={9}
                                className="w-full text-center text-3xl font-mono font-bold tracking-widest py-5 px-4 border-2 border-gray-200 rounded-2xl focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all uppercase placeholder-gray-300 text-gray-800"
                                disabled={isActivating}
                            />

                            <button
                                onClick={handleActivate}
                                disabled={isActivating || cleanPairingCode(pairingCode).length !== 8}
                                className={`w-full py-4 rounded-xl font-bold text-lg text-white transition-all flex items-center justify-center gap-3 ${isActivating || cleanPairingCode(pairingCode).length !== 8
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-emerald-500 to-green-600 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-emerald-200'
                                    }`}
                            >
                                {isActivating ? (
                                    <>
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                        <span>Conectando...</span>
                                    </>
                                ) : (
                                    <>
                                        <Power className="w-6 h-6" />
                                        <span>CONECTAR AGORA</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="px-4 pb-8 pt-4 border-t border-gray-100">
                <div className="text-center">
                    <img
                        src="https://locaninja.com/logotipo-watch.png"
                        alt="Watch Logo"
                        className="h-6 mx-auto mb-2 opacity-70"
                    />
                    <p className="text-xs text-gray-400">
                        © 2024 Watch Technologies Inc.
                    </p>
                </div>
            </div>

            {/* Estilo para animação */}
            <style>{`
                @keyframes slide-up {
                    from {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
                .animate-slide-up {
                    animation: slide-up 0.3s ease-out;
                }
            `}</style>
        </div>
    );
}

export default CompanionDashboard;
